import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, ArrowLeft, KeyRound, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);

  // Request password reset
  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setEmailSent(true);
      toast.success('If the email exists, a reset link has been sent.');
    } catch (err) {
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Complete password reset with token
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        token, 
        new_password: newPassword 
      });
      setResetComplete(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const PageWrapper = ({ children }) => (
    <>
      <Helmet>
        <title>Reset Password – ScoreCite</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {children}
    </>
  );

  // Success state after password reset
  if (resetComplete) {
    return (
      <PageWrapper>
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <Card className="bg-[#121212] border-white/10 w-full max-w-md">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Password Reset Complete</h2>
            <p className="text-zinc-400 mb-6">
              Your password has been successfully reset. You can now log in with your new password.
            </p>
            <Link to="/login">
              <Button className="w-full bg-blue-600 hover:bg-blue-500">
                Go to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
      </PageWrapper>
    );
  }

  // If we have a token, show the reset form
  if (token) {
    return (
      <PageWrapper>
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <Card className="bg-[#121212] border-white/10 w-full max-w-md">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-8 h-8 text-blue-400" />
            </div>
            <CardTitle className="text-2xl font-bold text-white">
              Set New Password
            </CardTitle>
            <p className="text-zinc-400 text-sm mt-2">
              Enter your new password below.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-zinc-400">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-[#0D0D0D] border-white/10 text-white pl-10"
                    placeholder="At least 8 characters"
                    required
                    minLength={8}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-zinc-400">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-[#0D0D0D] border-white/10 text-white pl-10"
                    placeholder="Repeat password"
                    required
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-500" 
                disabled={loading}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
      </PageWrapper>
    );
  }

  // Email sent success state
  if (emailSent) {
    return (
      <PageWrapper>
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <Card className="bg-[#121212] border-white/10 w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Mail className="w-16 h-16 text-blue-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Check Your Email</h2>
            <p className="text-zinc-400 mb-6">
              If an account with that email exists, we've sent a password reset link.
              The link expires in 1 hour.
            </p>
            <p className="text-zinc-500 text-sm mb-6">
              Didn't receive the email? Check your spam folder or try again.
            </p>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setEmailSent(false)}
                className="flex-1 border-white/20 text-white"
              >
                Try Again
              </Button>
              <Link to="/login" className="flex-1">
                <Button className="w-full bg-blue-600 hover:bg-blue-500">
                  Back to Login
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
      </PageWrapper>
    );
  }

  // Request reset form (no token)
  return (
    <PageWrapper>
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <Card className="bg-[#121212] border-white/10 w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-8 h-8 text-blue-400" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">
            Reset Password
          </CardTitle>
          <p className="text-zinc-400 text-sm mt-2">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRequestReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-400">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-[#0D0D0D] border-white/10 text-white pl-10"
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-500" 
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-zinc-400 hover:text-white text-sm inline-flex items-center">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
    </PageWrapper>
  );
}
